import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { MainHeaderComponent } from '../../components/main-header/main-header.component';
import { SidebarMode, SideBarComponent } from '../../components/side-bar/side-bar.component';
import { injectMediaQuery } from '../../utils/breakpoint.util';

/**
 * Debajo de este ancho la barra lateral se convierte en cajon (drawer). Punto
 * de ruptura propio de este layout, distinto de `MOBILE_BREAKPOINT_QUERY`
 * (767.98px) que usan los listados tabla/tarjetas — el cajon del sidebar
 * necesita más aire porque compite por ancho con el contenido, no solo con
 * una tabla.
 */
const DRAWER_QUERY = '(max-width: 1023.98px)';

/** Clave de persistencia del modo rail. */
const RAIL_STORAGE_KEY = 'ks-sidebar-rail';

/**
 * Shell de la consola autenticada: barra lateral fija + barra superior fija +
 * area de contenido.
 *
 * El estado del sidebar (rail / cajon) vive AQUI y baja por inputs, para que
 * ningun componente tenga que escribir en el DOM global.
 */
@Component({
  selector: 'app-dashboard-layout',
  imports: [RouterOutlet, SideBarComponent, MainHeaderComponent],
  templateUrl: './dashboard-layout.component.html',
  styleUrl: './dashboard-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.is-rail]': 'mode() === "rail"',
    '[class.is-overlay]': 'mode() === "overlay"',
    '(document:keydown.escape)': 'closeDrawer()',
  },
})
export class DashboardLayoutComponent {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  /** Ancho de cajon activo (movil / tablet). */
  private readonly isCompact = injectMediaQuery(DRAWER_QUERY);

  /** Rail solicitado por el usuario en escritorio. */
  private readonly railRequested = signal(this.readStoredRail());

  /** Cajon abierto (solo tiene sentido en modo `overlay`). */
  protected readonly drawerOpen = signal(false);

  protected readonly mode = computed<SidebarMode>(() => {
    if (this.isCompact()) return 'overlay';
    return this.railRequested() ? 'rail' : 'full';
  });

  constructor() {
    // Al salir del modo cajon, el cajon deja de tener sentido.
    effect(() => {
      if (!this.isCompact() && this.drawerOpen()) {
        this.drawerOpen.set(false);
      }
    });

    // El cuerpo no debe desplazarse por detras del cajon abierto.
    effect(() => {
      const locked = this.mode() === 'overlay' && this.drawerOpen();
      this.document.body.style.overflow = locked ? 'hidden' : '';
    });

    this.destroyRef.onDestroy(() => {
      this.document.body.style.overflow = '';
    });
  }

  protected openDrawer(): void {
    this.drawerOpen.set(true);
  }

  protected closeDrawer(): void {
    if (!this.drawerOpen()) return;

    this.drawerOpen.set(false);
    // Devuelve el foco al disparador para no perder al usuario de teclado.
    queueMicrotask(() => {
      this.document
        .querySelector<HTMLElement>('app-main-header .topbar__burger')
        ?.focus();
    });
  }

  protected toggleRail(): void {
    const next = !this.railRequested();
    this.railRequested.set(next);

    try {
      localStorage.setItem(RAIL_STORAGE_KEY, next ? '1' : '0');
    } catch {
      // Modo privado: la preferencia vive solo en memoria.
    }
  }

  private readStoredRail(): boolean {
    try {
      return localStorage.getItem(RAIL_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }
}
